import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "../components/ui/sonner";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import Layout from "../components/Layout";
import {
  Send, Search, Filter, MessageSquare, User, Bot, Clock, Bell,
  CheckCircle, Tag, Paperclip, Smile, FileText, Globe,
  Monitor, Smartphone, ChevronDown, ChevronLeft, AlertCircle, X, Info,
  MoreVertical, UserCheck, RefreshCw, ShieldOff, ShieldCheck
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace("https://", "wss://").replace("http://", "ws://");

const STATUS_LABELS = {
  active: "Aktif",
  waiting_agent: "Beklemede",
  open: "Açık",
  closed: "Kapalı",
  resolved: "Çözüldü",
  pending: "Beklemede",
};

// UTC suffix'i olmayan tarihleri doğru parse et (MySQL UTC → JS UTC)
function parseUTC(dateStr) {
  if (!dateStr) return new Date();
  // 'Z' veya '+xx:xx' yoksa MySQL UTC tarihidir, Z ekle
  if (!dateStr.endsWith('Z') && !/[+\-]\d{2}:\d{2}$/.test(dateStr)) {
    return new Date(dateStr + 'Z');
  }
  return new Date(dateStr);
}

function timeAgo(dateStr, t) {
  if (!dateStr) return "";
  const diff = Date.now() - parseUTC(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t("common.just_now");
  if (mins < 60) return `${mins}d ${t("common.ago")}`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}s ${t("common.ago")}`;
  return `${Math.floor(hours / 24)}g ${t("common.ago")}`;
}

// Basit markdown → HTML renderer (bot/agent mesajları için)
function renderMarkdown(raw) {
  if (!raw) return "";
  const escaped = raw
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  const lines = escaped.split("\n");
  const out = [];
  let inOl = false, inUl = false;
  const inline = (t) =>
    t
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(/_(.+?)_/g, "<em>$1</em>")
      .replace(/`(.+?)`/g, '<code style="background:#f1f5f9;padding:1px 4px;border-radius:3px;font-size:11px;font-family:monospace">$1</code>');
  for (const ln of lines) {
    const olM = ln.match(/^\s*(\d+)\.\s+(.*)$/);
    const ulM = ln.match(/^\s*[-*]\s+(.*)$/);
    const h1M = ln.match(/^#\s+(.*)$/);
    const h2M = ln.match(/^##\s+(.*)$/);
    const h3M = ln.match(/^###\s+(.*)$/);
    if (olM) {
      if (!inOl) { if (inUl) { out.push("</ul>"); inUl = false; } out.push('<ol style="padding-left:16px;margin:4px 0">'); inOl = true; }
      out.push(`<li style="margin:3px 0">${inline(olM[2])}</li>`);
    } else if (ulM) {
      if (!inUl) { if (inOl) { out.push("</ol>"); inOl = false; } out.push('<ul style="padding-left:16px;margin:4px 0">'); inUl = true; }
      out.push(`<li style="margin:3px 0">${inline(ulM[1])}</li>`);
    } else {
      if (inOl) { out.push("</ol>"); inOl = false; }
      if (inUl) { out.push("</ul>"); inUl = false; }
      if (h3M || h2M || h1M) {
        const hTxt = (h3M || h2M || h1M)[1];
        const sz = h1M ? "15px" : h2M ? "14px" : "13.5px";
        out.push(`<div style="font-weight:700;font-size:${sz};margin:6px 0 2px">${inline(hTxt)}</div>`);
      } else if (ln.trim() === "") {
        out.push('<div style="height:5px"></div>');
      } else {
        out.push(`<div>${inline(ln)}</div>`);
      }
    }
  }
  if (inOl) out.push("</ol>");
  if (inUl) out.push("</ul>");
  return out.join("");
}

export default function Inbox() {
  const { user, token } = useAuth();
  const { t } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();

  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [noteMode, setNoteMode] = useState(false);
  const [visitorTyping, setVisitorTyping] = useState(false);
  const [visitorTypingText, setVisitorTypingText] = useState("");
  const [newNote, setNewNote] = useState("");
  const [tags, setTags] = useState([]);
  const [cannedResponses, setCannedResponses] = useState([]);
  const [showCanned, setShowCanned] = useState(false);
  const [showBanModal, setShowBanModal] = useState(false);
  const [banReason, setBanReason] = useState("");
  const [uploadingFile, setUploadingFile] = useState(false);
  const [mobilePanel, setMobilePanel] = useState("list"); // 'list' | 'chat'
  const fileInputRef = useRef(null);

  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const reconnectRef = useRef(null);
  // Stale closure önleme: selectedConv'u her zaman güncel tutan ref
  const selectedConvRef = useRef(null);
  // URL'den uygulanan son conv parametresi — liste yenilenince eski conv'u tekrar açmasın
  const appliedConvRef = useRef(null);
  // handleWSMessage'ın en güncel versiyonunu tutan ref (closure stale sorununu tamamen giderir)
  const handleWSMessageRef = useRef(null);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try {
      const params = {};
      if (filter === "mine") params.assigned_to = "me";
      else if (filter === "unassigned") params.assigned_to = "unassigned";
      else if (filter !== "all") params.status = filter;

      const { data } = await axios.get(`${API}/conversations`, { params });
      setConversations(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 15000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  // selectedConvRef'i her zaman güncel tut (stale closure düzeltmesi)
  useEffect(() => {
    selectedConvRef.current = selectedConv;
  }, [selectedConv]);

  // Load selected conv from URL — yalnızca conv parametresi DEĞİŞTİĞİNDE uygula.
  // (Liste polling ile yenilendiğinde bu effect tekrar çalışıp eski conv'u açmasın diye
  //  son uygulanan değeri ref'te tutuyoruz.)
  useEffect(() => {
    const convId = searchParams.get("conv");
    if (convId && conversations.length > 0 && appliedConvRef.current !== convId) {
      const conv = conversations.find((c) => c.id === parseInt(convId));
      if (conv) selectConversation(conv);
    }
  }, [searchParams, conversations]);

  // WebSocket
  useEffect(() => {
    if (!token) return;
    connectAgentWS();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
    };
  }, [token]);

  // Push Notification state
  const [pushStatus, setPushStatus] = useState('unknown'); // unknown | granted | denied | default

  // Push: mount'ta mevcut izni kontrol et
  useEffect(() => {
    if (!token || !('Notification' in window)) return;
    const perm = Notification.permission;
    setPushStatus(perm);
    if (perm === 'granted') {
      // İzin zaten verilmiş → otomatik abone ol
      doSubscribe();
    }
  }, [token]);

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    return new Uint8Array([...raw].map(c => c.charCodeAt(0)));
  };

  // Push subscription oluştur ve backend'e kaydet
  const doSubscribe = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.warn('[Push] SW veya PushManager desteklenmiyor');
      return;
    }
    try {
      const { data } = await axios.get(`${API}/push/vapid-public-key`);
      const vapidKey = data.publicKey;

      const reg = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise((_, rej) => setTimeout(() => rej(new Error('SW timeout')), 8000)),
      ]);

      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
      }
      const subJson = sub.toJSON();
      if (!subJson.keys) throw new Error('Subscription keys eksik');

      await axios.post(
        `${API}/push/subscribe`,
        { endpoint: subJson.endpoint, keys: subJson.keys },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPushStatus('granted');
      toast.success('Bildirimler aktif! Yeni mesajlarda uyarı alacaksınız.');
    } catch (e) {
      console.error('[Push] Subscription hatası:', e?.message || e);
      toast.error(`Bildirim ayarlanamadı: ${e?.message || 'Bilinmeyen hata'}`);
    }
  };

  // Kullanıcı butona tıkladığında izin iste
  const requestPushPermission = async () => {
    if (!('Notification' in window)) return;
    const perm = await Notification.requestPermission();
    setPushStatus(perm);
    if (perm === 'granted') {
      await doSubscribe();
    }
  };

  // Kendine test push gönder
  const sendTestPush = async () => {
    try {
      await axios.post(
        `${API}/push/test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Test bildirimi gönderildi!');
    } catch {
      toast.error('Test gönderilemedi');
    }
  };

  const connectAgentWS = () => {
    // Zaten bağlı veya bağlanıyor ise tekrar bağlanma
    if (wsRef.current && wsRef.current.readyState <= WebSocket.OPEN) return;
    try {
      const ws = new WebSocket(`${WS_URL}/api/ws/agent?token=${token}`);
      wsRef.current = ws;

      ws.onopen = () => {
        // Keepalive: 25 saniyede bir ping gönder (proxy timeout önleme)
        if (ws._pingInterval) clearInterval(ws._pingInterval);
        ws._pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          } else {
            clearInterval(ws._pingInterval);
          }
        }, 25000);

        // Reconnect recovery: WS (yeniden) bağlandığında seçili konuşmadaki
        // kaçırılan mesajları API'den çek ve state'e merge et
        const conv = selectedConvRef.current;
        if (conv) {
          axios.get(`${API}/conversations/${conv.id}`)
            .then(({ data }) => {
              setMessages(prev => {
                const currentIds = new Set(
                  prev.filter(m => typeof m.id === "number").map(m => m.id)
                );
                const missed = (data.messages || []).filter(m => !currentIds.has(m.id));
                if (!missed.length) return prev;
                const merged = [...prev, ...missed].sort(
                  (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
                );
                return merged;
              });
            })
            .catch(() => {});
        }
      };

      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          // handleWSMessageRef üzerinden çağır → her zaman en güncel handler
          if (handleWSMessageRef.current) handleWSMessageRef.current(data);
        } catch {}
      };

      ws.onclose = () => {
        if (ws._pingInterval) clearInterval(ws._pingInterval);
        reconnectRef.current = setTimeout(connectAgentWS, 3000);
      };

      ws.onerror = () => ws.close();
    } catch {}
  };

  const handleWSMessage = (data) => {
    // selectedConvRef.current kullanarak stale closure'dan kaçınıyoruz
    const activeConvId = selectedConvRef.current?.id;

    if (data.type === "message") {
      const convId = data.conversation_id;
      if (activeConvId === convId) {
        setMessages((prev) => {
          // Eğer bu bizim gönderdiğimiz mesajın DB echo'su ise → optimistic'i replace et
          if (data.sender_type === "agent") {
            const optIdx = prev.findIndex(
              (m) => m._optimistic && m.content === data.content && m.sender_type === "agent"
            );
            if (optIdx >= 0) {
              const next = [...prev];
              next[optIdx] = data;
              return next;
            }
          }
          // Zaten varsa ekleme (başka kanaldan duplicate gelmişse)
          const exists = prev.find((m) => m.id === data.id);
          if (exists) return prev;
          return [...prev, data];
        });
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
      }
      setConversations((prev) =>
        prev.map((c) =>
          c.id === convId
            ? { ...c, last_message: { content: data.content, sender_type: data.sender_type, created_at: data.created_at }, updated_at: data.created_at, unread_count: activeConvId === convId ? 0 : (c.unread_count || 0) + 1 }
            : c
        )
      );
    } else if (data.type === "new_conversation") {
      fetchConversations();
    } else if (data.type === "visitor_typing") {
      if (data.conversation_id === activeConvId) {
        setVisitorTyping(true);
        setVisitorTypingText(data.text || "");
        clearTimeout(window._typingTimer);
        window._typingTimer = setTimeout(() => {
          setVisitorTyping(false);
          setVisitorTypingText("");
        }, 3000);
      }
    } else if (data.type === "agent_requested") {
      // Konuşmayı "waiting_agent" olarak güncelle
      setConversations(prev =>
        prev.map(c => c.id === data.conversation_id ? { ...c, status: "waiting_agent" } : c)
      );

    } else if (data.type === "conversation_ended") {
      // Ziyaretçi görüşmeyi sonlandırdı → konuşmayı resolved yap ve bildirim göster
      const endedConvId = data.conversation_id;
      setConversations(prev =>
        prev.map(c => c.id === endedConvId ? { ...c, status: "resolved" } : c)
      );
      // Seçili konuşmaya sistem mesajı ekle
      if (selectedConvRef.current?.id === endedConvId) {
        const sysMsg = {
          id: `sys_end_${Date.now()}`,
          content: "Ziyaretçi görüşmeyi sonlandırdı.",
          sender_type: "system",
          created_at: new Date().toISOString(),
        };
        setMessages(prev => [...prev, sysMsg]);
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
      }
    }
  };
  // Her render'da handleWSMessageRef'i güncelle → ws.onmessage her zaman güncel handler'ı çağırır
  handleWSMessageRef.current = handleWSMessage;

  const selectConversation = async (conv) => {
    appliedConvRef.current = String(conv.id); // URL guard ile senkron
    setSelectedConv(conv);
    setShowCanned(false);
    setMobilePanel("chat"); // mobilde chat paneline geç
    // URL'yi seçili konuşmayla senkronla (geçmişi şişirmeden)
    setSearchParams({ conv: String(conv.id) }, { replace: true });
    try {
      const { data } = await axios.get(`${API}/conversations/${conv.id}`);
      setMessages(data.messages || []);
      setSelectedConv(data);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);

      // Load tags and canned responses for site
      if (data.site_id) {
        const [tagsRes, cannedRes] = await Promise.all([
          axios.get(`${API}/tags?site_id=${data.site_id}`),
          axios.get(`${API}/canned-responses?site_id=${data.site_id}`)
        ]);
        setTags(tagsRes.data);
        setCannedResponses(cannedRes.data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConv) return;
    setSending(true);
    try {
      const content = newMessage.trim();
      setNewMessage("");

      // Send via WebSocket if connected
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        const tempId = `_tmp_${Date.now()}`;
        wsRef.current.send(JSON.stringify({
          type: "message",
          conversation_id: selectedConv.id,
          content
        }));
        // Optimistic add — string prefix ile gerçek DB ID'sinden ayırt edilir
        const optimistic = {
          id: tempId,
          content,
          sender_type: "agent",
          sender_id: String(user.id),
          sender_name: user.name,
          created_at: new Date().toISOString(),
          _optimistic: true
        };
        setMessages((prev) => [...prev, optimistic]);
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
      } else {
        // HTTP fallback
        const { data } = await axios.post(`${API}/conversations/${selectedConv.id}/messages`, { content });
        setMessages((prev) => [...prev, data]);
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSending(false);
    }
  };

  const sendNote = async () => {
    if (!newNote.trim() || !selectedConv) return;
    try {
      const { data } = await axios.post(`${API}/conversations/${selectedConv.id}/notes`, { content: newNote });
      setSelectedConv((prev) => ({ ...prev, notes: [...(prev.notes || []), data] }));
      setNewNote("");
      setNoteMode(false);
    } catch {}
  };

  const updateStatus = async (status) => {
    if (!selectedConv) return;
    await axios.put(`${API}/conversations/${selectedConv.id}/status`, { status });
    setSelectedConv((prev) => ({ ...prev, status }));
    setConversations((prev) => prev.map((c) => c.id === selectedConv.id ? { ...c, status } : c));
  };

  const assignToMe = async () => {
    if (!selectedConv) return;
    await axios.put(`${API}/conversations/${selectedConv.id}/assign`, { agent_id: user.id });
    setSelectedConv((prev) => ({ ...prev, assigned_agent_id: user.id, assigned_agent_name: user.name }));
    // WS üzerinden sistem mesajı gönder (server broadcast eder)
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "agent_joined",
        conversation_id: selectedConv.id,
        agent_name: user.name || user.email
      }));
    } else {
      // WS yoksa optimistik ekle
      setMessages((prev) => [...prev, {
        id: `_sys_${Date.now()}`,
        content: `${user.name || user.email} sohbete katıldı`,
        sender_type: "system",
        created_at: new Date().toISOString()
      }]);
    }
  };

  const banVisitorIP = async () => {
    if (!selectedConv?.visitor_ip) return;
    try {
      await axios.post(`${API}/bans`, {
        ip_address: selectedConv.visitor_ip,
        reason: banReason || "Manuel yasaklama"
      });
      setShowBanModal(false);
      setBanReason("");
      alert(`${selectedConv.visitor_ip} adresi yasaklandı.`);
    } catch (e) {
      alert(e?.response?.data?.detail || "IP zaten yasaklı olabilir.");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedConv) return;
    e.target.value = "";

    const allowed = ["image/jpeg", "image/png", "image/gif", "image/webp", "application/pdf"];
    if (!allowed.includes(file.type)) {
      alert("Sadece JPG, PNG ve PDF dosyaları yüklenebilir.");
      return;
    }

    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload/chat`, fd);

      // Send via WebSocket
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: "message",
          conversation_id: selectedConv.id,
          content: data.url,
          msg_type: data.file_type,
          filename: data.filename,
        }));
      }
    } catch (err) {
      alert("Dosya yüklenemedi.");
    } finally {
      setUploadingFile(false);
    }
  };

  const filtered = conversations
    .filter((c) =>
      search ? (c.visitor_name?.toLowerCase().includes(search.toLowerCase()) ||
        c.last_message?.content?.toLowerCase().includes(search.toLowerCase())) : true
    )
    .sort((a, b) => {
      // waiting_agent her zaman en üstte
      if (a.status === 'waiting_agent' && b.status !== 'waiting_agent') return -1;
      if (b.status === 'waiting_agent' && a.status !== 'waiting_agent') return 1;
      return 0;
    });

  const FILTERS = [
    { key: "all", label: t("inbox.all") },
    { key: "open", label: t("inbox.open") },
    { key: "pending", label: t("inbox.pending") },
    { key: "resolved", label: t("inbox.resolved") },
    { key: "unassigned", label: t("inbox.unassigned") },
    { key: "mine", label: t("inbox.mine") },
  ];

  return (
    <Layout>
      <div className="flex h-screen overflow-hidden" style={{ maxHeight: "100vh" }}>        {/* Left: Conversation List */}
        <div className={`${(mobilePanel === "chat" || mobilePanel === "detail") ? "hidden" : "flex"} md:flex flex-col w-full md:w-80 flex-shrink-0 border-r border-slate-200 bg-white`}>
          {/* Push Notification Banner */}
          {pushStatus === 'default' && 'Notification' in window && 'PushManager' in window && (
            <div className="mx-3 mt-3 mb-1 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
              <Bell size={15} className="text-amber-500 flex-shrink-0" />
              <span className="text-xs text-amber-800 flex-1 leading-snug">Yeni mesajlarda bildirim al</span>
              <button
                onClick={requestPushPermission}
                className="text-xs font-semibold text-white bg-amber-500 hover:bg-amber-600 px-2.5 py-1 rounded-lg transition-colors flex-shrink-0"
              >
                Aç
              </button>
            </div>
          )}
          {pushStatus === 'granted' && (
            <div className="mx-3 mt-3 mb-1 flex items-center justify-between bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2">
              <div className="flex items-center gap-1.5 text-xs text-emerald-700">
                <Bell size={13} className="flex-shrink-0" />
                <span>Bildirimler aktif</span>
              </div>
              <button
                onClick={sendTestPush}
                className="text-xs text-emerald-600 hover:text-emerald-800 underline"
              >
                Test Et
              </button>
            </div>
          )}
          {pushStatus === 'denied' && (
            <div className="mx-3 mt-3 mb-1 flex items-center gap-2 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
              <Bell size={13} className="text-red-400 flex-shrink-0" />
              <span className="text-xs text-red-600">Bildirimler engellendi. Tarayıcı ayarlarından izin verin.</span>
            </div>
          )}
          {pushStatus === 'unknown' && !('Notification' in window) && (
            <div className="mx-3 mt-3 mb-1 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
              <Bell size={13} className="text-slate-400 flex-shrink-0" />
              <span className="text-xs text-slate-500">Push bildirim bu tarayıcıda desteklenmiyor. iOS'ta Ana Ekran'dan açın.</span>
            </div>
          )}

          {/* Header */}
          <div className="p-4 border-b border-slate-100">
            <h1 className="font-heading font-bold text-slate-900 text-lg mb-3">{t("inbox.title")}</h1>
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("inbox.search")}
                className="w-full h-9 pl-9 pr-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-blue-400 bg-slate-50"
                data-testid="inbox-search"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="px-4 py-2 border-b border-slate-100 flex gap-1 overflow-x-auto">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  filter === f.key
                    ? "bg-blue-600 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
                data-testid={`filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-slate-400 text-sm animate-pulse">{t("common.loading")}</div>
            ) : filtered.length === 0 ? (
              <div className="p-8 text-center">
                <MessageSquare size={28} className="mx-auto text-slate-300 mb-2" />
                <p className="text-slate-500 text-sm">{t("inbox.no_convs")}</p>
              </div>
            ) : (
              filtered.map((conv) => (
                <div
                  key={conv.id}
                  onClick={() => selectConversation(conv)}
                  className={`p-4 border-b border-slate-50 cursor-pointer hover:bg-slate-50 transition-colors ${
                    selectedConv?.id === conv.id ? "bg-blue-50 border-l-2 border-l-blue-500" : ""
                  }`}
                  data-testid={`conv-item-${conv.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="relative">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                        {conv.visitor_name?.[0]?.toUpperCase() || "V"}
                      </div>
                      {conv.is_ai_handled && (
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center">
                          <Bot size={9} className="text-white" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm text-slate-800 truncate">
                          {conv.visitor_name || t("common.visitor")}
                        </span>
                        <span className="text-xs text-slate-400 ml-1 flex-shrink-0">
                          {timeAgo(conv.updated_at, t)}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 truncate mt-0.5">
                        {conv.last_message?.content || "..."}
                      </p>
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full status-${conv.status}`}>
                          {(conv.status === 'active' || conv.status === 'waiting_agent') && (
                            <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse flex-shrink-0" />
                          )}
                          {STATUS_LABELS[conv.status] || conv.status}
                        </span>
                        {conv.site_name && (
                          <span className="text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full truncate max-w-[90px]">
                            {conv.site_name}
                          </span>
                        )}
                        {conv.unread_count > 0 && (
                          <span className="ml-auto w-5 h-5 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center font-bold">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Center: Chat */}
        <div className={`${(mobilePanel === "list" || mobilePanel === "detail") ? "hidden" : "flex"} md:flex flex-1 flex-col bg-slate-50 overflow-hidden`}>
          {selectedConv ? (
            <>
              {/* Chat header */}
              <div className="bg-white border-b border-slate-200 px-3 md:px-5 py-3 flex items-center gap-2 md:gap-3 flex-shrink-0">
                {/* Mobile back button */}
                <button
                  className="md:hidden p-1.5 -ml-1 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                  onClick={() => setMobilePanel("list")}
                  data-testid="mobile-back-btn"
                >
                  <ChevronLeft size={20} />
                </button>
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-sm font-bold">
                  {selectedConv.visitor_name?.[0]?.toUpperCase() || "V"}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900 text-sm">
                    {selectedConv.visitor_name || t("common.visitor")}
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-2">
                    {selectedConv.site_name && <span>{selectedConv.site_name}</span>}
                    {selectedConv.visitor_country && <span>• {selectedConv.visitor_country}</span>}
                    {selectedConv.is_ai_handled && (
                      <span className="flex items-center gap-1 text-emerald-600">
                        <Bot size={11} /> {t("inbox.ai_handled")}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {/* Mobil: ziyaretçi bilgileri paneli açma butonu */}
                  <button
                    className="lg:hidden p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                    onClick={() => setMobilePanel("detail")}
                    title="Ziyaretçi Bilgileri"
                    data-testid="mobile-detail-btn"
                  >
                    <Info size={18} />
                  </button>
                  {selectedConv.assigned_agent_id !== user.id && (
                    <button
                      onClick={assignToMe}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-lg hover:bg-blue-100 transition-colors"
                      data-testid="assign-to-me-btn"
                    >
                      <UserCheck size={13} /> {t("inbox.assign_to_me")}
                    </button>
                  )}
                  {(selectedConv.status === "open" || selectedConv.status === "active" || selectedConv.status === "waiting_agent") ? (
                    <button
                      onClick={() => updateStatus("resolved")}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-lg hover:bg-emerald-100 transition-colors"
                      data-testid="resolve-btn"
                    >
                      <CheckCircle size={13} /> {t("inbox.resolve")}
                    </button>
                  ) : selectedConv.status === "resolved" || selectedConv.status === "closed" ? (
                    <button
                      onClick={() => updateStatus("open")}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 text-slate-700 text-xs font-medium rounded-lg hover:bg-slate-100 transition-colors"
                      data-testid="reopen-btn"
                    >
                      <RefreshCw size={13} /> {t("inbox.reopen")}
                    </button>
                  ) : null}
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-5 space-y-3">
                {messages.map((msg, i) => (
                  <div
                    key={msg.id || i}
                    data-testid={`message-${msg.id}`}
                  >
                    {/* Sistem mesajı (görüşme sonlandı vb.) */}
                    {msg.sender_type === "system" ? (
                      <div className="flex justify-center my-2">
                        <span className="text-xs text-slate-400 bg-slate-100 px-3 py-1 rounded-full border border-slate-200">
                          {msg.content}
                        </span>
                      </div>
                    ) : (
                  <div
                    className={`flex ${msg.sender_type === "visitor" ? "justify-start" : "justify-end"}`}
                  >
                    {msg.sender_type === "visitor" && (
                      <div className="w-7 h-7 rounded-full bg-slate-300 flex items-center justify-center text-xs text-slate-600 font-bold mr-2 flex-shrink-0 self-end mb-5">
                        {selectedConv.visitor_name?.[0]?.toUpperCase() || "V"}
                      </div>
                    )}
                    <div className={`max-w-xs lg:max-w-md ${msg.sender_type === "visitor" ? "" : "items-end flex flex-col"}`}>
                      <div className={`px-4 py-2.5 text-sm leading-relaxed ${
                        msg.sender_type === "visitor" ? "bubble-agent" :
                        msg.sender_type === "bot" ? "bubble-bot" : "bubble-visitor"
                      }`}>
                        {msg.msg_type === "image" ? (
                          <img
                            src={msg.content}
                            alt={msg.filename || "görsel"}
                            className="max-w-[220px] max-h-[180px] rounded-lg object-cover cursor-pointer block"
                            onClick={() => window.open(msg.content, "_blank")}
                          />
                        ) : msg.msg_type === "file" ? (
                          <a
                            href={msg.content}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                          >
                            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                              <FileText size={16} />
                            </div>
                            <span className="text-sm underline break-all">{msg.filename || "Dosyayı İndir"}</span>
                          </a>
                        ) : (
                          (msg.sender_type === "bot" || msg.sender_type === "agent")
                            ? <div dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }} className="md-content" />
                            : msg.content
                        )}
                      </div>
                      <div className={`text-xs text-slate-400 mt-1 flex items-center gap-1 ${msg.sender_type === "visitor" ? "" : "justify-end"}`}>
                        {msg.sender_type === "agent" && (msg.sender_name || "Temsilci")}
                        {msg.sender_type === "bot" && <span className="flex items-center gap-0.5"><Bot size={10} /> AI Bot</span>}
                        {msg.created_at && (
                          <span>{parseUTC(msg.created_at).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}</span>
                        )}
                      </div>
                    </div>
                    {(msg.sender_type === "agent" || msg.sender_type === "bot") && (
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ml-2 flex-shrink-0 self-end mb-5 ${msg.sender_type === "bot" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
                        {msg.sender_type === "bot" ? <Bot size={12} /> : (msg.sender_name?.[0] || "T")}
                      </div>
                    )}
                  </div>
                  )}
                  </div>
                ))}
                <div ref={messagesEndRef} />

                {/* Visitor typing indicator */}
                {visitorTyping && (
                  <div className="flex justify-start px-4 pb-2">
                    <div className="flex items-start gap-2 max-w-[75%]">
                      <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs flex-shrink-0 mt-1">Z</div>
                      <div className="flex flex-col gap-1">
                        {visitorTypingText ? (
                          <div className="bg-white border-2 border-dashed border-slate-300 rounded-2xl px-4 py-2.5 shadow-sm">
                            <p className="text-sm text-slate-500 italic">{visitorTypingText}</p>
                            <div className="flex items-center gap-1 mt-1.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{animationDelay:'0ms'}} />
                              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{animationDelay:'150ms'}} />
                              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{animationDelay:'300ms'}} />
                            </div>
                          </div>
                        ) : (
                          <div className="bg-white border border-slate-200 rounded-2xl px-4 py-2.5 shadow-sm flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{animationDelay:'0ms'}} />
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{animationDelay:'150ms'}} />
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{animationDelay:'300ms'}} />
                          </div>
                        )}
                        <span className="text-xs text-slate-400 px-1">yazıyor...</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Notes section */}
              {selectedConv.notes?.length > 0 && (
                <div className="px-5 pb-2">
                  {selectedConv.notes.map((note) => (
                    <div key={note.id} className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-2 text-xs">
                      <div className="flex items-center gap-1 text-amber-700 font-medium mb-1">
                        <Info size={11} /> {t("inbox.internal_note")} - {note.user_name}
                      </div>
                      <p className="text-amber-800">{note.content}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Input area */}
              <div className="bg-white border-t border-slate-200 p-4 flex-shrink-0">
                {/* Canned responses */}
                {showCanned && cannedResponses.length > 0 && (
                  <div className="mb-3 bg-white border border-slate-200 rounded-xl shadow-lg max-h-40 overflow-y-auto">
                    {cannedResponses.map((cr) => (
                      <button
                        key={cr.id}
                        onClick={() => { setNewMessage(cr.content); setShowCanned(false); }}
                        className="w-full text-left px-4 py-2.5 hover:bg-slate-50 border-b border-slate-50 last:border-0 transition-colors"
                        data-testid={`canned-${cr.id}`}
                      >
                        <div className="font-medium text-sm text-slate-800">{cr.title}</div>
                        <div className="text-xs text-slate-500 truncate">{cr.content}</div>
                      </button>
                    ))}
                  </div>
                )}

                {noteMode ? (
                  <div className="space-y-2">
                    <textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder={t("inbox.internal_note")}
                      className="w-full rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm focus:outline-none focus:border-amber-400 resize-none"
                      rows={3}
                      data-testid="note-input"
                    />
                    <div className="flex gap-2">
                      <button onClick={sendNote} className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors" data-testid="send-note-btn">
                        {t("inbox.add_note")}
                      </button>
                      <button onClick={() => setNoteMode(false)} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 transition-colors">
                        {t("common.cancel")}
                      </button>
                    </div>
                  </div>
                ) : selectedConv.assigned_agent_id && selectedConv.assigned_agent_id !== user.id ? (
                  /* Başka agent bu konuşmayı yönetiyor — join prompt */
                  <div className="flex items-center justify-between gap-3 py-2 px-1" data-testid="join-prompt">
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {selectedConv.assigned_agent_name?.[0] || "T"}
                      </div>
                      <span>
                        <span className="font-medium text-slate-700">{selectedConv.assigned_agent_name}</span>
                        {" "}bu konuşmayı yönetiyor
                      </span>
                    </div>
                    <button
                      onClick={assignToMe}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors flex-shrink-0"
                      data-testid="takeover-btn"
                    >
                      <UserCheck size={13} /> Devral
                    </button>
                  </div>
                ) : !selectedConv.assigned_agent_id ? (
                  /* Atanmamış konuşma — join prompt */
                  <div className="flex items-center justify-between gap-3 py-2 px-1" data-testid="unassigned-prompt">
                    <p className="text-sm text-slate-500">Yanıtlamak için sohbete katılın</p>
                    <button
                      onClick={assignToMe}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors flex-shrink-0"
                      data-testid="join-conv-btn"
                    >
                      <UserCheck size={13} /> Sohbete Katıl
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2 items-end">
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                      onChange={handleFileUpload}
                      data-testid="file-input"
                    />
                    <div className="flex-1">
                      <textarea
                        value={newMessage}
                        onChange={(e) => {
                          setNewMessage(e.target.value);
                          if (wsRef.current?.readyState === WebSocket.OPEN) {
                            wsRef.current.send(JSON.stringify({ type: "typing", conversation_id: selectedConv.id }));
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
                        }}
                        placeholder={t("inbox.type_message")}
                        className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400 resize-none bg-slate-50"
                        rows={2}
                        data-testid="message-input"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <button
                        onClick={() => setShowCanned(!showCanned)}
                        className="w-9 h-9 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 flex items-center justify-center transition-colors"
                        title={t("inbox.quick_replies")}
                        data-testid="canned-toggle-btn"
                      >
                        <FileText size={15} />
                      </button>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingFile}
                        className="w-9 h-9 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 flex items-center justify-center transition-colors disabled:opacity-50"
                        title="Dosya ekle (JPG, PNG, PDF)"
                        data-testid="attach-file-btn"
                      >
                        {uploadingFile ? (
                          <RefreshCw size={15} className="animate-spin" />
                        ) : (
                          <Paperclip size={15} />
                        )}
                      </button>
                      <button
                        onClick={() => setNoteMode(true)}
                        className="w-9 h-9 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 flex items-center justify-center transition-colors"
                        title={t("inbox.add_note")}
                        data-testid="note-toggle-btn"
                      >
                        <Info size={15} />
                      </button>
                      <button
                        onClick={sendMessage}
                        disabled={!newMessage.trim() || sending}
                        className="w-9 h-9 rounded-lg bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-colors disabled:opacity-50"
                        data-testid="send-message-btn"
                      >
                        <Send size={15} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
                  <MessageSquare size={28} className="text-blue-400" />
                </div>
                <h3 className="font-heading font-semibold text-slate-700 mb-2">Konuşma Seçin</h3>
                <p className="text-sm text-slate-500">Sol panelden bir konuşmayı seçin</p>
              </div>
            </div>
          )}
        </div>

        {/* Right: Visitor Info */}
        {selectedConv && (
          <div className={`${mobilePanel === "detail" ? "flex flex-col w-full" : "hidden"} lg:flex lg:flex-col lg:w-64 flex-shrink-0 border-l border-slate-200 bg-white overflow-y-auto`}>
            {/* Mobil geri başlık */}
            <div className="lg:hidden flex items-center gap-2 px-3 py-3 border-b border-slate-100 sticky top-0 bg-white z-10">
              <button
                onClick={() => setMobilePanel("chat")}
                className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                data-testid="mobile-detail-back-btn"
              >
                <ChevronLeft size={20} />
              </button>
              <span className="text-sm font-semibold text-slate-900">{t("inbox.visitor_info")}</span>
            </div>
            <div className="p-4">
              <h3 className="font-heading font-semibold text-slate-900 text-sm mb-4 hidden lg:block">{t("inbox.visitor_info")}</h3>

              <div className="space-y-3 text-sm">
                {[
                  { label: "IP", value: selectedConv.visitor_ip },
                  { label: "Ülke", value: selectedConv.visitor_country },
                  { label: "Cihaz", value: selectedConv.visitor_device },
                  { label: "Tarayıcı", value: selectedConv.visitor_browser },
                  { label: "Email", value: selectedConv.visitor_email },
                ].filter((f) => f.value).map((field) => (
                  <div key={field.label}>
                    <div className="text-xs text-slate-400 mb-0.5">{field.label}</div>
                    <div className="text-slate-700 font-medium text-xs break-all">{field.value}</div>
                  </div>
                ))}

                {selectedConv.visitor_device && (
                  <div className="flex items-center gap-2 py-2 px-3 bg-slate-50 rounded-lg">
                    {selectedConv.visitor_device === "Mobile" ? (
                      <Smartphone size={14} className="text-slate-500 flex-shrink-0" />
                    ) : (
                      <Monitor size={14} className="text-slate-500 flex-shrink-0" />
                    )}
                    <div>
                      <div className="text-xs font-medium text-slate-700">{selectedConv.visitor_device}</div>
                      {selectedConv.visitor_browser && (
                        <div className="text-xs text-slate-400">{selectedConv.visitor_browser}</div>
                      )}
                    </div>
                  </div>
                )}

                {selectedConv.assigned_agent_name && (
                  <div>
                    <div className="text-xs text-slate-400 mb-0.5">Atanan Agent</div>
                    <div className="text-slate-700 font-medium text-xs">{selectedConv.assigned_agent_name}</div>
                  </div>
                )}
              </div>

              {/* IP Ban Button */}
              {selectedConv.visitor_ip && (
                <div className="mt-4">
                  <button
                    onClick={() => setShowBanModal(true)}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-50 text-red-600 text-xs font-medium rounded-lg hover:bg-red-100 transition-colors border border-red-100"
                    data-testid="ban-ip-btn"
                  >
                    <ShieldOff size={13} /> IP Yasakla ({selectedConv.visitor_ip})
                  </button>
                </div>
              )}

              {/* CSAT Rating */}
              {selectedConv.rating && (
                <div className="mt-5 p-3 bg-amber-50 rounded-xl border border-amber-100" data-testid="csat-rating-panel">
                  <div className="text-xs text-slate-500 mb-2 font-medium">Memnuniyet Puanı</div>
                  <div className="flex items-center gap-1 mb-1.5">
                    {[1,2,3,4,5].map((s) => (
                      <span key={s} className={`text-lg ${s <= selectedConv.rating ? 'text-amber-400' : 'text-slate-200'}`}>★</span>
                    ))}
                    <span className="ml-1 text-sm font-semibold text-slate-700">{selectedConv.rating}/5</span>
                  </div>
                  {selectedConv.rating_comment && (
                    <p className="text-xs text-slate-600 italic">"{selectedConv.rating_comment}"</p>
                  )}
                  {selectedConv.rated_at && (
                    <p className="text-xs text-slate-400 mt-1">
                      {new Date(selectedConv.rated_at).toLocaleDateString("tr-TR")}
                    </p>
                  )}
                </div>
              )}

              {/* Tags */}
              {tags.length > 0 && (
                <div className="mt-5">
                  <div className="text-xs text-slate-400 mb-2">{t("inbox.tags")}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {tags.map((tag) => (
                      <button
                        key={tag.id}
                        onClick={async () => {
                          const current = selectedConv.tags || [];
                          const next = current.includes(tag.name)
                            ? current.filter((t) => t !== tag.name)
                            : [...current, tag.name];
                          await axios.put(`${API}/conversations/${selectedConv.id}/tags`, { tags: next });
                          setSelectedConv((prev) => ({ ...prev, tags: next }));
                        }}
                        className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                          (selectedConv.tags || []).includes(tag.name)
                            ? "border-transparent text-white"
                            : "border-slate-200 text-slate-600 hover:border-slate-300"
                        }`}
                        style={(selectedConv.tags || []).includes(tag.name) ? { background: tag.color } : {}}
                        data-testid={`tag-btn-${tag.id}`}
                      >
                        {tag.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Pages */}
              {selectedConv.visitor_pages?.length > 0 && (
                <div className="mt-5">
                  <div className="text-xs text-slate-400 mb-2">{t("inbox.pages_visited")}</div>
                  <div className="space-y-1">
                    {selectedConv.visitor_pages.slice(-5).map((p, i) => (
                      <div key={i} className="text-xs text-slate-600 truncate" title={p.url}>
                        <Globe size={10} className="inline mr-1 text-slate-400" />
                        {p.url?.replace(/^https?:\/\//, "").slice(0, 35)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Ban IP Modal */}
      {showBanModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowBanModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="ban-modal">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                <ShieldOff size={20} className="text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">IP Adresi Yasakla</h3>
                <p className="text-xs text-slate-500">{selectedConv?.visitor_ip}</p>
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Sebep (zorunlu)</label>
              <input
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                placeholder="Örn: Spam, uygunsuz içerik..."
                className="w-full h-11 rounded-xl border border-slate-200 px-4 text-sm focus:outline-none focus:border-red-400"
                data-testid="ban-reason-input"
                autoFocus
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={banVisitorIP}
                disabled={!banReason.trim()}
                className="flex-1 py-2.5 bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50"
                data-testid="ban-confirm-btn"
              >
                Yasakla
              </button>
              <button
                onClick={() => { setShowBanModal(false); setBanReason(""); }}
                className="flex-1 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-200 transition-colors"
              >
                Vazgeç
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
